import type { ComponentType } from "react";
import type { FormValues } from "@/widgets/classic-form/form";
import { ArrayField } from "@/widgets/classic-form/form";
import type { FieldMapperProps } from "@/features/fields-grid";
import { publishAddField, publishRemoveField } from "@/features/form-fields";
import { getEventInstancePath, getEventInstancesPath } from "@/entities/data-model";
import type { EventAssertion } from "@/entities/form-metadata";
import { getRowInstancesPaths } from "@/entities/form-metadata";
import { type FieldPath, useFormAPI } from "@/shared/forms";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";
import { AddNewEventFieldPanel } from "../Panel/AddNewEventFieldPanel";
import { resolveAddRowFocusTarget } from "../addRowFocusTarget";
import type { BindableEventFieldsGroup, BindableEventFieldset } from "../rowPrimaryIdentity";
import { EventInstanceRow } from "./EventInstanceRow";
import type { YearRangeGridConfig } from "./gridLayout";
import styles from "./YearRangeEventGroup.module.scss";

interface YearRangeEventGroupProps {
    /** Already narrowed to fieldsets that name an identity; see `toBindableFieldsets`. */
    fieldGroup: BindableEventFieldsGroup;
    FieldMapper: ComponentType<FieldMapperProps<EventAssertion>>;
    gridConfig: YearRangeGridConfig;
}

export const YearRangeEventGroup: React.FC<YearRangeEventGroupProps> = ({ fieldGroup, FieldMapper, gridConfig }) => {
    const { fieldsets, assertions } = fieldGroup;

    const track = useTrackSearchFormInteraction();

    const coupledPathsOf = (fieldset: BindableEventFieldset, primaryPath: string) =>
        getRowInstancesPaths(fieldset).filter((path) => path !== primaryPath);

    const onRemoveItem = (params: {
        fieldName: FieldPath<FormValues>;
        index: number;
        fieldset: BindableEventFieldset;
    }) => {
        track({ objectName: "remove_event" });
        publishRemoveField({
            fieldName: params.fieldName,
            index: params.index,
            coupledFieldNames: coupledPathsOf(params.fieldset, params.fieldName),
        });
    };
    const addNew = publishAddField;
    const { getFieldValue } = useFormAPI();

    const handleItemAdded = (fieldset: BindableEventFieldset) => {
        // On a mixed row, the entry to append-and-focus isn't necessarily the primary identity.
        const target = resolveAddRowFocusTarget(fieldset, assertions);
        if (!target) return;

        const { instancesPath, mappedAssertion } = target;
        const instances = (getFieldValue(instancesPath) as Array<unknown>) || [];
        const newItemIndex = instances.length;

        addNew({
            fieldName: instancesPath,
            coupledFieldNames: coupledPathsOf(fieldset, instancesPath),
            type: "event",
            newItemIndex,
            mappedAssertion,
        });
    };

    return (
        <div className={styles.root}>
            {gridConfig.Removable && <AddNewEventFieldPanel fieldsets={fieldsets} onItemAdded={handleItemAdded} />}

            <ul className="listRuled">
                {fieldsets.map((fieldset) => {
                    const arrayPath = getEventInstancesPath(fieldset.primaryIdentity) as FieldPath<FormValues>;

                    return (
                        <ArrayField key={arrayPath} name={arrayPath}>
                            {({ items }) =>
                                items?.map((_, index) => {
                                    const fieldPath = getEventInstancePath(fieldset.primaryIdentity, index);
                                    return (
                                        <li className={styles.listItem} key={`${arrayPath}-${index}`}>
                                            <EventInstanceRow
                                                instanceIndex={index}
                                                fieldset={fieldset}
                                                fieldPath={fieldPath}
                                                onRemove={() => onRemoveItem({ fieldName: arrayPath, index, fieldset })}
                                                FieldMapper={FieldMapper}
                                                gridConfig={gridConfig}
                                            />
                                        </li>
                                    );
                                })
                            }
                        </ArrayField>
                    );
                })}
            </ul>
        </div>
    );
};
